import * as React from 'react'
import { cn } from '../../lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

interface AccordionContextValue {
  expanded: string | null
  setExpanded: (value: string | null) => void
  allowMultiple: boolean
  expandedItems: Set<string>
  toggleItem: (value: string) => void
}

const AccordionContext = React.createContext<AccordionContextValue | undefined>(undefined)

function useAccordion() {
  const context = React.useContext(AccordionContext)
  if (!context) {
    throw new Error('Accordion components must be used within an Accordion')
  }
  return context
}

// Accordion Root
export interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: 'single' | 'multiple'
  defaultValue?: string | string[]
  collapsible?: boolean
}

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, type = 'single', defaultValue, collapsible = true, children, ...props }, ref) => {
    const [expanded, setExpanded] = React.useState<string | null>(
      type === 'single' && typeof defaultValue === 'string' ? defaultValue : null
    )
    const [expandedItems, setExpandedItems] = React.useState<Set<string>>(
      type === 'multiple' && Array.isArray(defaultValue) ? new Set(defaultValue) : new Set()
    )

    const toggleItem = React.useCallback(
      (value: string) => {
        if (type === 'single') {
          if (expanded === value && collapsible) {
            setExpanded(null)
          } else {
            setExpanded(value)
          }
        } else {
          setExpandedItems((prev) => {
            const next = new Set(prev)
            if (next.has(value)) {
              next.delete(value)
            } else {
              next.add(value)
            }
            return next
          })
        }
      },
      [type, expanded, collapsible]
    )

    return (
      <AccordionContext.Provider
        value={{
          expanded,
          setExpanded,
          allowMultiple: type === 'multiple',
          expandedItems,
          toggleItem,
        }}
      >
        <div ref={ref} className={cn('space-y-2', className)} {...props}>
          {children}
        </div>
      </AccordionContext.Provider>
    )
  }
)

Accordion.displayName = 'Accordion'

// Accordion Item
export interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, value, children, ...props }, ref) => {
    const { expanded, expandedItems, allowMultiple } = useAccordion()
    const isExpanded = allowMultiple ? expandedItems.has(value) : expanded === value

    return (
      <div
        ref={ref}
        data-state={isExpanded ? 'open' : 'closed'}
        className={cn(
          'rounded-2xl border border-warm-200 bg-white overflow-hidden transition-colors',
          isExpanded && 'border-copper-200',
          className
        )}
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<{ itemValue?: string }>, {
              itemValue: value,
            })
          }
          return child
        })}
      </div>
    )
  }
)

AccordionItem.displayName = 'AccordionItem'

// Accordion Trigger
export interface AccordionTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  itemValue?: string
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ className, children, itemValue, ...props }, ref) => {
    const { expanded, expandedItems, allowMultiple, toggleItem } = useAccordion()
    const isExpanded = allowMultiple
      ? expandedItems.has(itemValue || '')
      : expanded === itemValue

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex w-full items-center justify-between px-6 py-4 font-heading font-medium text-midnight-900',
          'hover:bg-warm-50 transition-colors text-left',
          className
        )}
        onClick={() => itemValue && toggleItem(itemValue)}
        aria-expanded={isExpanded}
        {...props}
      >
        {children}
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 text-warm-500" />
        </motion.div>
      </button>
    )
  }
)

AccordionTrigger.displayName = 'AccordionTrigger'

// Accordion Content
export interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {
  itemValue?: string
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ className, children, itemValue, ...props }, ref) => {
    const { expanded, expandedItems, allowMultiple } = useAccordion()
    const isExpanded = allowMultiple
      ? expandedItems.has(itemValue || '')
      : expanded === itemValue

    return (
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            ref={ref}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className={cn('px-6 pb-4 text-warm-600', className)} {...props}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }
)

AccordionContent.displayName = 'AccordionContent'

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
